import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { draftEditGuard } from './core/guards/draft-edit.guard';
import { profileCompleteGuard } from './core/guards/profile-complete.guard';
import { wizardLeaveGuard } from './core/guards/wizard-leave.guard';

export const routes: Routes = [
  // ─── Public (unauthenticated) ──────────────────────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('./public/public-shell/public-shell.component').then(
        (m) => m.PublicShellComponent
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./public/landing/landing.component').then(
            (m) => m.LandingComponent
          ),
      },
      {
        path: 'publications',
        loadComponent: () =>
          import('./public/publications/public-publications.component').then(
            (m) => m.PublicPublicationsComponent
          ),
      },
      {
        path: 'publications/:reference',
        loadComponent: () =>
          import('./public/publications/public-publication-detail.component').then(
            (m) => m.PublicPublicationDetailComponent
          ),
      },
      {
        path: 'verify',
        loadComponent: () =>
          import('./public/verify/public-verify.component').then(
            (m) => m.PublicVerifyComponent
          ),
      },
      {
        path: 'newspapers',
        loadComponent: () =>
          import('./public/newspapers/public-newspapers.component').then(
            (m) => m.PublicNewspapersComponent
          ),
      },
      {
        path: 'categories',
        loadComponent: () =>
          import('./public/categories/public-categories.component').then(
            (m) => m.PublicCategoriesComponent
          ),
      },
      {
        path: 'pricing',
        loadComponent: () =>
          import('./public/pricing/public-pricing.component').then(
            (m) => m.PublicPricingComponent
          ),
      },
      {
        path: 'about',
        loadComponent: () =>
          import('./public/about/public-about.component').then(
            (m) => m.PublicAboutComponent
          ),
      },
      {
        path: 'faq',
        loadComponent: () =>
          import('./public/faq/public-faq.component').then(
            (m) => m.PublicFaqComponent
          ),
      },
      {
        path: 'contact',
        loadComponent: () =>
          import('./public/contact/public-contact.component').then(
            (m) => m.PublicContactComponent
          ),
      },
    ],
  },

  // ─── Authenticated app shell ───────────────────────────────────────────
  {
    path: 'app',
    canActivate: [authGuard, roleGuard],
    canActivateChild: [roleGuard],
    loadComponent: () =>
      import('./layout/app-shell/app-shell.component').then(
        (m) => m.AppShellComponent
      ),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'publications',
        loadComponent: () =>
          import('./features/publications/publications-list.component').then(
            (m) => m.PublicationsListComponent
          ),
      },
      {
        path: 'publications/new',
        // Re-run the guards even when re-clicking "New Publication" or
        // switching between ?id= values without a full navigation.
        runGuardsAndResolvers: 'always',
        canActivate: [profileCompleteGuard, draftEditGuard],
        canDeactivate: [wizardLeaveGuard],
        loadComponent: () =>
          import('./features/wizard/publication-wizard.component').then(
            (m) => m.PublicationWizardComponent
          ),
      },
      {
        path: 'publications/:id',
        loadComponent: () =>
          import('./features/publications/publication-detail.component').then(
            (m) => m.PublicationDetailComponent
          ),
      },
      {
        path: 'drafts',
        loadComponent: () =>
          import('./features/drafts/drafts.component').then(
            (m) => m.DraftsComponent
          ),
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/payments/payments.component').then(
            (m) => m.PaymentsComponent
          ),
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./features/payments/payments.component').then(
            (m) => m.PaymentsComponent
          ),
      },
      {
        path: 'bulk-upload',
        data: { title: 'Bulk Upload' },
        loadComponent: () =>
          import('./features/placeholder/placeholder.component').then(
            (m) => m.PlaceholderComponent
          ),
      },
      {
        path: 'verified-items',
        data: { title: 'My Verified Items' },
        loadComponent: () =>
          import('./features/placeholder/placeholder.component').then(
            (m) => m.PlaceholderComponent
          ),
      },
      {
        path: 'payments/callback',
        loadComponent: () =>
          import('./features/payment-callback/payment-callback.component').then(
            (m) => m.PaymentCallbackComponent
          ),
      },
      {
        path: 'wallet',
        loadComponent: () =>
          import('./features/wallet/wallet.component').then(
            (m) => m.WalletComponent
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notifications/notifications.component').then(
            (m) => m.NotificationsComponent
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            (m) => m.ProfileComponent
          ),
      },
      {
        path: 'verify',
        loadComponent: () =>
          import('./features/verify/verify.component').then(
            (m) => m.VerifyComponent
          ),
      },
    ],
  },

  // ─── Fallback ──────────────────────────────────────────────────────────
  { path: '**', redirectTo: '' },
];
